import * as React from "react";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { EmailInput } from "../../../ui/components/Form";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { useI18n } from "../../lib/i18n";
import * as productAccessApi from "../../lib/api/product-access";
import type { ProductAccessTier } from "../../lib/api/product-access";

const INVITE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidInviteEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && INVITE_EMAIL_PATTERN.test(trimmed);
}

export function InviteProductAccessModal({
  open,
  setOpen,
  productId,
  productName,
  token,
  onInvited,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  productId: string;
  productName: string;
  token: string | null;
  onInvited?: () => void;
}) {
  const { t } = useI18n();
  const [email, setEmail] = React.useState("");
  const [tier, setTier] = React.useState<ProductAccessTier>("view");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [showEmailValidation, setShowEmailValidation] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setEmail("");
      setTier("view");
      setError(null);
      setSuccess(null);
      setLoading(false);
      setShowEmailValidation(false);
    }
  }, [open]);

  const trimmedEmail = email.trim();
  const emailValid = isValidInviteEmail(email);
  const emailFieldError =
    showEmailValidation && trimmedEmail.length > 0 && !emailValid
      ? "Please enter a valid email address (e.g. name@company.com)"
      : undefined;
  const canInvite = Boolean(token) && emailValid;

  const handleInvite = async () => {
    if (!token || !canInvite) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await productAccessApi.inviteProductAccess(token, productId, {
        email: trimmedEmail,
        tier,
        productName,
      });
      setSuccess(`Invitation sent to ${trimmedEmail}`);
      onInvited?.();
      setEmail("");
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to send invitation"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} minWidth="480px" maxWidth="520px">
      <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 8 }}>
        <h3 style={{ margin: 0 }}>{t("pro.products.inviteToProduct")}</h3>
        <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
          Invite someone to access <strong>{productName}</strong>. External
          users receive product access only — they are not added to the
          organization.
        </p>

        {error ? <Callout variant="error">{error}</Callout> : null}
        {success ? <Callout variant="success">{success}</Callout> : null}

        <EmailInput
          label={t("pro.users.email")}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setShowEmailValidation(false);
            setError(null);
            setSuccess(null);
          }}
          onBlur={() => setShowEmailValidation(true)}
          error={emailFieldError}
          disabled={loading}
        />

        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{t("pro.products.accessLevel")}</span>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as ProductAccessTier)}
            disabled={loading}
            style={{
              padding: "8px 10px",
              borderRadius: 6,
              border: "1px solid var(--color-border, #e5e7eb)",
            }}
          >
            {productAccessApi.PRODUCT_ACCESS_TIER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={loading}
          >
            Close
          </Button>
          <Button
            type="button"
            color="primary"
            onClick={() => void handleInvite()}
            disabled={!canInvite || loading}
          >
            Send invite
          </Button>
        </div>
      </div>
    </Modal>
  );
}
